import React, { useEffect, useRef, useState } from "react";
import "./invite-gate-exact.css";
import wordmark from "../images/FPW_Name.png";
import monogram from "../images/FPW_Logo.png";
import InviteFieldInline from "./InviteFieldInline";
import InviteRequestForm from "./InviteRequestForm";

type Props = {
  /** LocalStorage key that gates the site */
  storageKey?: string;         // default "fpw_beta"
  /** Optional callback after acceptance */
  onAccepted?: () => void;
};

/**
 * FPW Invite Gate — EXACT MATCH to the provided mock.
 * - Pure React + CSS. No libraries. No backend.
 * - Any non-empty input "unlocks" the beta:
 *     localStorage["fpw_beta"] = "1"
 *     localStorage["fpw_invite_code"] = "<NORMALIZED_CODE>"
 * - Reads ?invite=<CODE> to prefill.
 * - Bypasses automatically if localStorage["fpw_beta"] === "1".
 * - Copies, colors, spacing, and images match the screenshot.
 */
const InviteGateExact: React.FC<Props> = ({ storageKey = "fpw_beta", onAccepted }) => {
  const [open, setOpen] = useState<boolean>(() => {
    const hasAccessValue = hasAccess(storageKey);
    console.log('[GATE] storageKey:', storageKey);
    console.log('[GATE] hasAccess result:', hasAccessValue);
    console.log('[GATE] localStorage value:', localStorage.getItem(storageKey));
    console.log('[GATE] should open gate:', !hasAccessValue);
    return !hasAccessValue;
  });
  const [code, setCode] = useState<string>("");
  const [showRequestForm, setShowRequestForm] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  console.log('[GATE] component render - open:', open);

  // Lock background scroll while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (open) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Prefill from ?invite=...
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const q = p.get("invite");
      if (q) setCode(normalize(q));
    } catch {}
  }, []);

  if (!open) return null;

  function accept() {
    const value = normalize(code);
    console.log('[GATE] accept() called with code:', code);
    console.log('[GATE] normalized value:', value);
    if (!value) { 
      console.log('[GATE] no value, focusing input');
      inputRef.current?.focus(); 
      return; 
    }
    if (value !== 'GETCRAFTING') {
      console.log('[GATE] wrong code, expected GETCRAFTING, got:', value);
      inputRef.current?.focus();
      return;
    }
    console.log('[GATE] correct code, setting localStorage');
    try {
      localStorage.setItem(storageKey, "1");
      localStorage.setItem("fpw_invite_code", value);
      console.log('[GATE] localStorage set successfully');
    } catch (e) {
      console.log('[GATE] localStorage error:', e);
    }
    console.log('[GATE] closing gate');
    setOpen(false);
    onAccepted?.();
  }

  return (
    <div className="fpw-overlay fpw-ui" role="dialog" aria-modal="true">
      {/* Header with exact wordmark image and a subtle right-side pill */}
      <header className="fpw-header">
        <img
          src={wordmark}
          className="fpw-wordmark"
          alt="Free Pattern Warehouse"
        />
        <div className="fpw-beta-pill">Invite-Only Beta</div>
      </header>

      {/* Center card with lock, headings, input, helper, links, monogram */}
      <main className="fpw-main">
        <section style={{
          width: "100%",
          maxWidth: 720,
          borderRadius: 18,
          background: "rgba(8, 26, 27, 0.55)",
          border: "1px solid rgba(125, 209, 198, 0.18)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.50), inset 0 1px 0 rgba(255,255,255,0.04)",
          padding: 28,
          color: "#E9F6F3",
          margin: "0 auto",
        }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            display: "grid",
            placeItems: "center",
            margin: "0 auto 10px",
            background: "rgba(30,182,161,0.14)",
            border: "1px solid rgba(30,182,161,0.32)",
          }} aria-hidden>
            {/* simple lock glyph to match the mock */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="5" y="10" width="14" height="10" rx="2" stroke="rgba(30,182,161,0.9)" strokeWidth="2"/>
              <path d="M8 10V8a4 4 0 1 1 8 0v2" stroke="rgba(30,182,161,0.9)" strokeWidth="2" fill="none" />
            </svg>
          </div>

          <h1 style={{
            textAlign: "center",
            fontSize: 22,
            lineHeight: "28px",
            fontWeight: 700,
            margin: "0 0 8px 0",
          }}>Welcome to our private beta</h1>
          <div style={{
            textAlign: "center",
            fontSize: 14,
            lineHeight: "18px",
            letterSpacing: ".28em",
            textTransform: "uppercase",
            opacity: 0.9,
            marginBottom: 14,
          }}>Invite-Only Beta</div>

          <div style={{
            marginBottom: 16
          }}>
            <label style={{
              display: "block",
              fontSize: 13,
              color: "#D9F3EC",
              marginBottom: 8
            }}>Enter your invite code</label>
            <InviteFieldInline
              value={code}
              onChange={setCode}
              onSubmit={accept}
              placeholder="FPW-ABCD-1234"
              buttonText="Unlock Beta"
            />
          </div>

          <div style={{
            textAlign: "center",
            color: "#96B2AD",
            fontSize: 15,
            lineHeight: "22px",
            margin: "10px 0 8px",
          }}>Your code is shareable with a few friends...</div>

          <div style={{
            display: "flex",
            justifyContent: "space-between",
            color: "#96B2AD",
            fontSize: 15,
            lineHeight: "22px",
            marginTop: 6,
          }}>
            <a href="#" onClick={(e) => {
              e.preventDefault();
              setShowRequestForm(true);
            }} style={{
              color: "#96B2AD",
              textDecoration: "none",
            }}>I need a code</a>

          </div>
        </section>
        
        {/* Invite request form (conditionally shown) */}
        {showRequestForm && (
          <section style={{
            width: "100%",
            maxWidth: 720,
            borderRadius: 18,
            background: "rgba(8, 26, 27, 0.55)",
            border: "1px solid rgba(125, 209, 198, 0.18)",
            boxShadow: "0 30px 80px rgba(0,0,0,0.50), inset 0 1px 0 rgba(255,255,255,0.04)",
            padding: 28,
            color: "#E9F6F3",
            margin: "20px auto 0",
          }}>
            <InviteRequestForm onBack={() => setShowRequestForm(false)} />
          </section>
        )}
        
        {/* Monogram under center container */}
        <div className="fpw-footer">
          <img src={monogram} className="fpw-monogram" alt="FPW" />
        </div>

        {/* Notice message */}
        <div style={{
          textAlign: "center",
          color: "#E8A317",
          fontSize: 13,
          lineHeight: "18px",
          margin: "16px auto 20px",
          padding: "8px 12px",
          background: "rgba(232, 163, 23, 0.1)",
          border: "1px solid rgba(232, 163, 23, 0.2)",
          borderRadius: 8,
          maxWidth: 720,
        }}>Please note: In the event that beta users find critical issues, we will freeze new user requests until the issue is resolved.</div>
      </main>
    </div>
  );
};

export default InviteGateExact;

function normalize(v:string){ 
  const result = v.replace(/\s+/g,"").replace(/—/g,"-").toUpperCase();
  console.log('[GATE] normalize:', v, '->', result);
  return result;
}
function hasAccess(key:string){ 
  try { 
    const value = localStorage.getItem(key);
    const result = value === "1";
    console.log('[GATE] hasAccess check - key:', key, 'value:', value, 'result:', result);
    return result;
  } catch (e) { 
    console.log('[GATE] hasAccess error:', e);
    return false; 
  } 
}